import { useCallback, useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

import { getConcern, listSlaDue } from '../../services/projectRegisterApi';
import RiskDetailPanel from './RiskDetailPanel';
import {
  Chip,
  DrawerDetail,
  EmptyState,
  ErrorBanner,
  RISK_TYPE_TONE,
  Spinner,
  T,
  personLabel,
  shortRef,
  useDetailNav,
} from './shared';

const SlaRow = ({ concern, breached, selected, onClick }) => {
  const riskTone = RISK_TYPE_TONE[concern.risk_type] || {
    fg: T.textSec,
    bg: 'rgba(209,209,199,0.08)',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3.5 rounded-2xl p-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40 ${
        selected
          ? 'border border-indigo-500/60 bg-indigo-950/20 shadow-md shadow-indigo-950/30 ring-1 ring-indigo-500/50'
          : 'border border-zinc-800/80 bg-zinc-900/80 shadow-sm hover:border-zinc-700 hover:bg-zinc-800/60'
      }`}
    >
      <Chip fg={riskTone.fg} bg={riskTone.bg}>
        {concern.risk_type || concern.subtype}
      </Chip>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-zinc-100">
          {concern.description || shortRef(concern.entity_id)}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-zinc-400">
          {personLabel(concern.responsible_person_id) || 'Unassigned'}
        </p>
      </div>
      <Chip
        fg={breached ? T.danger : T.textSec}
        bg={breached ? T.dangerBg : 'rgba(212,212,216,0.08)'}
      >
        {concern.sla_breach_at
          ? new Date(concern.sla_breach_at).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
            })
          : '—'}
      </Chip>
    </button>
  );
};

/** SLA tab: concerns approaching or past their `sla_breach_at` window. */
const SlaList = ({ accessToken }) => {
  const [breached, setBreached] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    selectedId,
    detail,
    loading: detailLoading,
    error: detailError,
    open: openDetail,
    close: closeDetail,
  } = useDetailNav((entityId) => getConcern(accessToken, entityId));

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [breachedResponse, upcomingResponse] = await Promise.all([
        listSlaDue(accessToken, { window: 'breached' }),
        listSlaDue(accessToken, { window: 'upcoming' }),
      ]);
      setBreached(breachedResponse?.concerns || []);
      setUpcoming(upcomingResponse?.concerns || []);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading) return <Spinner />;

  return (
    // Same fixed-header/scrolling-body split as the other register tabs --
    // no filter bar here, but the error banner (when present) still stays
    // put above the scroll instead of paging away with the sections below.
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ErrorBanner>{error}</ErrorBanner>

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {breached.length === 0 && upcoming.length === 0 ? (
          <EmptyState icon={Clock}>
            Nothing on an SLA clock right now.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-3">
            {breached.length > 0 && (
              <section>
                <div className="mb-1.5 flex items-center gap-2">
                  <span
                    className="text-[10px] tracking-wide"
                    style={{ color: T.danger }}
                  >
                    Breached
                  </span>
                  <span className="text-[10px]" style={{ color: T.muted }}>
                    {breached.length}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {breached.map((concern) => (
                    <SlaRow
                      key={concern.entity_id}
                      concern={concern}
                      breached
                      selected={concern.entity_id === selectedId}
                      onClick={() => openDetail(concern.entity_id)}
                    />
                  ))}
                </div>
              </section>
            )}

            <section>
              <div className="mb-1.5 flex items-center gap-2">
                <span
                  className="text-[10px] tracking-wide"
                  style={{ color: T.muted }}
                >
                  Upcoming
                </span>
                <span className="text-[10px]" style={{ color: T.muted }}>
                  {upcoming.length}
                </span>
              </div>
              {upcoming.length === 0 ? (
                <EmptyState icon={Clock}>Nothing else due soon.</EmptyState>
              ) : (
                <div className="flex flex-col gap-2">
                  {upcoming.map((concern) => (
                    <SlaRow
                      key={concern.entity_id}
                      concern={concern}
                      breached={false}
                      selected={concern.entity_id === selectedId}
                      onClick={() => openDetail(concern.entity_id)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      <DrawerDetail open={!!selectedId} onClose={closeDetail}>
        {detailLoading ? (
          <Spinner />
        ) : detailError ? (
          <div className="p-6">
            <ErrorBanner>{detailError}</ErrorBanner>
          </div>
        ) : (
          <RiskDetailPanel
            accessToken={accessToken}
            detail={detail}
            onClose={closeDetail}
          />
        )}
      </DrawerDetail>
    </div>
  );
};

export default SlaList;
